import type {
  ColumnOrderState,
  ColumnPinningState,
  OnChangeFn,
  VisibilityState
} from "@tanstack/react-table";
import { Columns3 } from "lucide-react";
import { SCREENER_COLUMN_IDS } from "../lib/screener-state";
import type { ScreenerDensity } from "../lib/types";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export const SCREENER_COLUMN_LABELS: Record<(typeof SCREENER_COLUMN_IDS)[number], string> = {
  favorite: "Favorite",
  ticker: "Ticker",
  exchange: "Exchange",
  companyName: "Company",
  industryDescription: "Industry",
  insiderName: "Insider",
  relationshipFlags: "Relationship",
  signal: "Signal",
  confidence: "Confidence",
  filingDate: "Filed",
  tradeDate: "Traded",
  filingLagDays: "Filing lag",
  shares: "Shares",
  price: "Price",
  value: "Value",
  holdingsBefore: "Holdings before",
  holdingsAfter: "Holdings after",
  ownershipChangePct: "Holdings change",
  materialityBps: "Materiality",
  distinctInsiderCount: "Cluster insiders",
  transactionCode: "Code",
  planStatus: "Plan",
  securityStatus: "Security",
  valueQuality: "Value quality",
  flags: "Flags",
  score: "Score",
  details: "Details"
};

interface TableControlsProps {
  density: ScreenerDensity;
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
  columnPinning: ColumnPinningState;
  onDensityChange: (density: ScreenerDensity) => void;
  onColumnVisibilityChange: OnChangeFn<VisibilityState>;
  onColumnOrderChange: OnChangeFn<ColumnOrderState>;
  onColumnPinningChange: OnChangeFn<ColumnPinningState>;
}

function moveColumn(order: ColumnOrderState, columnId: string, delta: -1 | 1): ColumnOrderState {
  const currentIndex = order.indexOf(columnId);
  if (currentIndex < 0) return order;
  const nextIndex = Math.max(0, Math.min(order.length - 1, currentIndex + delta));
  if (nextIndex === currentIndex) return order;
  const next = [...order];
  const [column] = next.splice(currentIndex, 1);
  next.splice(nextIndex, 0, column!);
  return next;
}

function pinColumn(
  pinning: ColumnPinningState,
  columnId: string,
  side: "left" | "right" | false
): ColumnPinningState {
  const left = (pinning.left ?? []).filter((id) => id !== columnId);
  const right = (pinning.right ?? []).filter((id) => id !== columnId);
  if (side === "left") left.push(columnId);
  if (side === "right") right.push(columnId);
  return { left, right };
}

export function TableControls({
  density,
  columnVisibility,
  columnOrder,
  columnPinning,
  onDensityChange,
  onColumnVisibilityChange,
  onColumnOrderChange,
  onColumnPinningChange
}: TableControlsProps) {
  return (
    <>
      <div className="inline-flex rounded-md border border-border bg-surface-inset p-0.5" role="group" aria-label="Table density">
        {(["compact", "comfortable"] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-label={`${option === "compact" ? "Compact" : "Comfortable"} density`}
            aria-pressed={density === option}
            className="rounded px-2 py-1 text-[11px] capitalize text-muted-foreground aria-pressed:bg-accent-soft aria-pressed:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            onClick={() => onDensityChange(option)}
          >
            {option}
          </button>
        ))}
      </div>

      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" aria-label="Columns">
            <Columns3 className="h-3.5 w-3.5" aria-hidden="true" />
            Columns
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          role="dialog"
          aria-label="Column controls"
          className="max-h-[70vh] w-[min(720px,calc(100vw-2rem))] overflow-y-auto"
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-xs font-semibold">Visible columns and layout</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onColumnPinningChange({ left: [], right: [] })}
            >
              Unpin all
            </Button>
          </div>
          <div className="grid gap-1 sm:grid-cols-2">
            {SCREENER_COLUMN_IDS.map((columnId) => {
              const label = SCREENER_COLUMN_LABELS[columnId];
              const orderIndex = columnOrder.indexOf(columnId);
              const pinnedLeft = (columnPinning.left ?? []).includes(columnId);
              const pinnedRight = (columnPinning.right ?? []).includes(columnId);
              return (
                <div
                  key={columnId}
                  className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded border border-border px-2 py-1.5"
                >
                  <label className="flex min-w-0 items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      aria-label={`Show ${label}`}
                      checked={columnVisibility[columnId] !== false}
                      onChange={() =>
                        onColumnVisibilityChange((current) => ({
                          ...current,
                          [columnId]: current[columnId] === false
                        }))
                      }
                    />
                    <span className="truncate">{label}</span>
                  </label>
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      className="rounded px-1.5 py-1 text-[10px] text-muted-foreground hover:text-foreground disabled:opacity-30"
                      aria-label={`Move ${label} left`}
                      disabled={orderIndex <= 0}
                      onClick={() => onColumnOrderChange((current) => moveColumn(current, columnId, -1))}
                    >
                      ←
                    </button>
                    <button
                      type="button"
                      className="rounded px-1.5 py-1 text-[10px] text-muted-foreground hover:text-foreground disabled:opacity-30"
                      aria-label={`Move ${label} right`}
                      disabled={orderIndex < 0 || orderIndex >= columnOrder.length - 1}
                      onClick={() => onColumnOrderChange((current) => moveColumn(current, columnId, 1))}
                    >
                      →
                    </button>
                    <button
                      type="button"
                      className="rounded px-1.5 py-1 text-[10px] text-muted-foreground hover:text-foreground"
                      aria-label={pinnedLeft ? `Unpin ${label}` : `Pin ${label} left`}
                      onClick={() =>
                        onColumnPinningChange((current) =>
                          pinColumn(current, columnId, pinnedLeft ? false : "left")
                        )
                      }
                    >
                      L
                    </button>
                    <button
                      type="button"
                      className="rounded px-1.5 py-1 text-[10px] text-muted-foreground hover:text-foreground"
                      aria-label={pinnedRight ? `Unpin ${label}` : `Pin ${label} right`}
                      onClick={() =>
                        onColumnPinningChange((current) =>
                          pinColumn(current, columnId, pinnedRight ? false : "right")
                        )
                      }
                    >
                      R
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
}
